import {
  CODE_ALTLEFT,
  CODE_ALTRIGHT,
  CODE_ARROWDOWN,
  CODE_ARROWLEFT,
  CODE_ARROWRIGHT,
  CODE_ARROWUP,
  CODE_BACKSPACE,
  CODE_CAPSLOCK,
  CODE_CTRLLEFT,
  CODE_CTRLRIGHT,
  CODE_DELETE,
  CODE_END,
  CODE_ENTER,
  CODE_ESCAPE,
  CODE_HOME,
  CODE_METALEFT,
  CODE_METARIGHT,
  CODE_PRINTSCREEN,
  CODE_SHIFTLEFT,
  CODE_SHIFTRIGHT,
  CODE_TAB,
} from "common/constants";
import truncateStringWithEllipses from "common/utils/truncateStringWithEllipses";
import type { DOMAttributes, ReactNode, MouseEvent, SyntheticEvent } from "react";
import { useEffect, useImperativeHandle, useRef, useState } from "react";
import HorizontalRule from "ui/components/HorizontalRule";
import type { ThumbnailProps } from "ui/components/Thumbnail";
import Thumbnail from "ui/components/Thumbnail";
import cx from "ui/cx";
import type { FormFieldState } from "ui/forms/formFieldState";
import type { AutocompleteField } from "ui/forms/Input";
import type { InputRef } from "ui/forms/Input";
import Input from "ui/forms/Input";
import useOutsideClick from "ui/hooks/useOutsideClick";
import IconCheck from "ui/icons/IconCheck";
import IconChevron from "ui/icons/IconChevron";
import IconCircle from "ui/icons/IconCircle";
import IconClose from "ui/icons/IconClose";
import IconLocked from "ui/icons/IconLocked";
import IconMiniChevronDown from "ui/icons/IconMiniChevronDown";
import Tooltip from "ui/popovers/Tooltip";
import styles from "./Select.module.scss";

type SelectSize = "small" | "medium" | "mediumLarge" | "large";

export interface SelectRef {
  readonly input: InputRef;
  readonly options: HTMLDivElement;
  validate: () => string;
}

export function optionPropsFromStringArray(array: readonly string[]): OptionProps[] {
  return array.map((v) => ({ value: v, label: v }));
}

export interface OptionProps<T = string | number | boolean | null> {
  labelIcon?: ReactNode;
  label: string;
  value: T;
  subLabel?: string;

  // Options marked with "intermediate" are just suggestions to help with the search from a large set of dynamically generated values, not actually selectable values.
  intermediate?: boolean;

  // Use option value to populate input text. By default option label is input
  intermediateValue?: boolean;

  // If specified, the option is no longer selectable but is still displayed according to the desired behavior
  displayBehavior?: "header" | "ruler" | "disabled" | "locked";
  // Special-case selectable behavior that should not affect displayBehavior semantics.
  specialBehavior?: "addNew";

  color?: string;

  thumbnail?: ThumbnailProps;
  icon?: ReactNode;
  // To set a tooltip different than label - is also included in search
  title?: string;

  /** When true, renders labelIcon after the label, right-aligned */
  placeLabelIconRight?: boolean;

  /** When set, this option shows a right-arrow and opens a submenu on hover with these options. Selection is from the submenu only. */
  subOptions?: OptionProps[];

  indent?: number;
}

type SelectMouseEvents = keyof Pick<DOMAttributes<HTMLDivElement>, "onMouseDown" | "onClick">;
export type SelectPropsOnChangeFunc = (value: string | number | boolean | null) => void;

export interface SelectProps {
  id?: string;
  options: OptionProps[] | ((userInput: string) => OptionProps[]);
  labelFromValue?: (value?: string | number | boolean | null) => string | undefined;
  value?: string | number | boolean | null;
  placeholder?: string;
  onChange?: SelectPropsOnChangeFunc;
  clearable?: boolean;
  disabled?: boolean;
  lockIcon?: boolean;
  title?: string;
  ariaLabel?: string;
  selectSize?: SelectSize;
  className?: string;
  autoComplete?: AutocompleteField;
  openImmediately?: boolean;
  clearOnChange?: boolean;
  persistOnChange?: boolean;
  additiveOption?: OptionProps;
  showScrollbar?: boolean;
  showTooltip?: boolean;
  tooltipText?: string;
  tooltipDelay?: number;
  showOptionsGap?: boolean;
  showSelectedOptionCheck?: boolean;
  onOpen?: () => void;
  /** Called when the options dropdown closes (so the consumer can e.g. close a submenu). */
  onClose?: () => void;
  transparent?: boolean;
  optionsMaxWidth?: number;
  optionsMaxHeight?: number;
  // Enables typing a value into the text input that doesn't exist in options
  allowInputWithoutSelection?: boolean;
  optionsClassName?: string;
  inputState?: FormFieldState;

  inputOnChange?: (value: string | number | boolean) => void;
  /**
   * Formats the input value while entering it.
   */
  inputFormatter?: (value: string) => string;
  /**
   * In varying contexts, we may want to use either onMouseDown or onClick. This is
   * a bit unfortunate, but due to the way that certain click events are handled within
   * Studio, this is necessary to allow for flexibility and to achieve desired behavior.
   */
  mouseEvent?: SelectMouseEvents;
  /**
   * An indicator to hide the input element and reposition the options list so that
   * the options list is flush with an arbitrary parent node.
   */
  hideInput?: boolean;
  /**
   * When an option has subOptions, hovering it calls this (open); the consumer shows a submenu.
   * onSubmenuMouseLeaveRow is called when the pointer leaves the row so the consumer can delay-close.
   */
  onSubmenuOpen?: (params: {
    anchorRect: DOMRect;
    subOptions: OptionProps[];
    onSelect: (value: string | number | boolean | null) => void;
  }) => void;
  onSubmenuMouseLeaveRow?: () => void;
  ref?: React.Ref<SelectRef>;
  dataTestId?: string;
}

const OPTION_PADDING_LEFT = 9;
const OPTION_INDENT_STEP = 12;

function stopNativeMouseEvent(event: globalThis.MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

function suppressNextMouseUpAndClick() {
  // Mouse-down selection can close the menu before mouse-up; swallow that release so it cannot hit the UI underneath.
  const stopClick = (event: globalThis.MouseEvent) => {
    stopNativeMouseEvent(event);
    window.removeEventListener("click", stopClick, true);
  };

  const stopMouseUp = (event: globalThis.MouseEvent) => {
    stopNativeMouseEvent(event);
    window.addEventListener("click", stopClick, true);
    window.setTimeout(() => window.removeEventListener("click", stopClick, true), 0);
  };

  window.addEventListener("mouseup", stopMouseUp, { capture: true, once: true });
}

function SelectComponent({
  id,
  title,
  ariaLabel,
  value,
  onChange,
  clearable = false,
  disabled = false,
  lockIcon = false,
  options,
  labelFromValue,
  placeholder,
  selectSize = "large",
  className = "",
  autoComplete,
  openImmediately = false,
  clearOnChange = false,
  persistOnChange = false,
  showScrollbar = false,
  showTooltip = false,
  tooltipDelay,
  showOptionsGap = false,
  showSelectedOptionCheck = false,
  onOpen,
  transparent,
  mouseEvent = "onMouseDown",
  additiveOption,
  allowInputWithoutSelection,
  inputOnChange,
  inputFormatter,
  hideInput = false,
  optionsMaxWidth,
  optionsMaxHeight,
  optionsClassName,
  inputState,
  onSubmenuOpen,
  onSubmenuMouseLeaveRow,
  onClose,
  ref,
  dataTestId,
  tooltipText,
}: SelectProps) {
  /** Options with specialBehavior "addNew" are shown when there are no matching standard options in the menu. */
  let defaultOptions: OptionProps[];

  if (typeof options === "function") {
    if (!(labelFromValue !== undefined)) {
      console.assert(false);
    }
    defaultOptions = options("");
  } else {
    defaultOptions = options;
  }

  const isAddNewOption = (o: OptionProps) => o.specialBehavior === "addNew";

  const optionsExcludingAddNew: OptionProps[] =
    typeof options === "function" ? defaultOptions : defaultOptions.filter((o) => !isAddNewOption(o));
  const addNewOptionsOnly: OptionProps[] =
    typeof options === "function" ? [] : defaultOptions.filter((o) => isAddNewOption(o));
  const optionsExcludingAddNewForEmptyState: OptionProps[] = optionsExcludingAddNew.filter(
    (o) => o.displayBehavior !== "ruler" && o.displayBehavior !== "disabled" && o.displayBehavior !== "locked",
  );
  const defaultFilteredOptions: OptionProps[] =
    addNewOptionsOnly.length > 0 && optionsExcludingAddNewForEmptyState.length === 0
      ? addNewOptionsOnly
      : optionsExcludingAddNew;

  const inputTextFromValue = () => {
    if (typeof options === "function") {
      return labelFromValue?.(value) || "";
    } else {
      if (value === undefined) {
        return "";
      }
      for (const i of options) {
        if (value === null && i.value === null && i.displayBehavior) {
          continue;
        }
        if (typeof value === "number" ? Math.abs((i.value as number) - value) < 0.001 : i.value === value) {
          return i.label;
        }
      }

      return value?.toString() ?? "";
    }
  };

  const [openMenu, setOpenMenu] = useState(openImmediately);
  const [filteredOptions, setFilteredOptions] = useState<OptionProps[]>(defaultOptions);
  const [optionIndex, setOptionIndex] = useState(0);
  const [inputText, setInputText] = useState(inputTextFromValue());
  const isInputFocusedRef = useRef(false);
  const inputRef = useRef<InputRef>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);
  const [up, setUp] = useState<boolean>(false);

  useOutsideClick([optionsListRef], () => {
    if (typeof options === "function") {
      return;
    }
    setOpenMenu(false);
    onClose?.();
  });

  const setInputTextFromValue = () => {
    const text = inputTextFromValue();
    if (text || !allowInputWithoutSelection) {
      setInputText(text);
    }
  };

  useEffect(() => {
    if (!isInputFocusedRef.current) {
      // Set the value shown on the input in case the state changes, but only if the user is not actively typing.
      setInputTextFromValue();
    }
  }, [value, options]);

  // Allow consumer components to access the refs for both the input and options
  // elements by passing in a single ref. This allows consumer components to implement
  // custom styling logic - as an example, see the ExpandableSelect component.
  useImperativeHandle(
    ref ?? { current: null },
    () => ({
      input: inputRef.current as InputRef,
      options: optionsRef.current as HTMLDivElement,
      validate: (): string => {
        if (!inputRef.current) {
          throw new Error("Input ref not available!");
        }
        return inputRef.current.validate();
      },
    }),
    [inputRef, optionsRef],
  );

  useEffect(() => {
    if (openMenu) {
      onOpen?.();
      inputRef.current?.focus();
    }
    const listToShow = addNewOptionsOnly.length > 0 ? defaultFilteredOptions : defaultOptions;
    setFilteredOptions(listToShow);
    const i = listToShow.findIndex((opt) => opt.value === value);
    setOptionIndex(i >= 0 ? i : 0);
  }, [openMenu, value, options]);

  const closeMenu = () => {
    if (persistOnChange) {
      return;
    }

    setOpenMenu(false);
    onClose?.();
  };

  const scrollToReveal = (scrollTarget: HTMLElement) => {
    if (optionsListRef.current && scrollTarget) {
      const parent = optionsListRef.current;
      const targetBottom = scrollTarget.offsetTop + scrollTarget.offsetHeight;
      const targetTop = scrollTarget.offsetTop;

      if (targetTop < parent.scrollTop) {
        parent.scrollTo({ top: targetTop });
      } else if (targetBottom > parent.scrollTop + parent.offsetHeight) {
        parent.scrollTo({ top: targetBottom - parent.offsetHeight });
      }
    }
  };

  useEffect(() => {
    if (optionsListRef.current && openMenu) {
      const activeElements = optionsListRef.current.getElementsByClassName(styles.active);
      if (activeElements.length > 0) {
        scrollToReveal(activeElements[0] as HTMLElement);
      }
    }

    if (optionsRef.current && openMenu) {
      const box = optionsRef.current.getBoundingClientRect();
      const clippedContainer = optionsRef.current.closest("[data-clipped-container]");

      if (clippedContainer) {
        const containerBox = clippedContainer.getBoundingClientRect();
        if (box.bottom > containerBox.bottom) {
          setUp(true);
        }
      } else if (box.bottom > window.innerHeight) {
        setUp(true);
      }
    }
  }, [openMenu, optionIndex]);

  const updateActive = (i: number) => {
    setOptionIndex(i);

    if (filteredOptions[i]) {
      const text = filteredOptions[i].value !== undefined ? filteredOptions[i].label : "";
      setInputText(text);
    }
  };

  const handleSelect = (chosenOption: OptionProps, i?: number) => {
    if (i !== undefined) {
      setOptionIndex(i);
    }
    if (chosenOption === undefined) {
      setInputTextFromValue();
      return;
    }
    if (chosenOption.displayBehavior) {
      return;
    }
    if (typeof options === "function" && chosenOption.intermediate) {
      if (chosenOption.intermediateValue === true) {
        setInputText(chosenOption.value as string);
        setFilteredOptions(options(chosenOption.value as string));
      } else {
        setInputText(chosenOption.label);
        setFilteredOptions(options(chosenOption.label));
      }
      setOptionIndex(0);
      return;
    }
    if (clearOnChange) {
      setInputText("");
    } else {
      setInputText(chosenOption.label);
    }
    onChange?.(chosenOption.value);
    closeMenu();
  };

  const activeOption: OptionProps | undefined = !persistOnChange ? defaultOptions[optionIndex] : undefined;
  const hasThumbnail: boolean = (activeOption?.thumbnail && activeOption?.label === inputText) || false;
  const tooltipEnabled = showTooltip && inputText;
  const showClearControl: boolean = clearable && !hideInput;
  const canClear: boolean = showClearControl && !lockIcon && !disabled && !!inputText;

  const handleClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setInputText("");
    setFilteredOptions(addNewOptionsOnly.length > 0 ? defaultFilteredOptions : defaultOptions);
    setOptionIndex(0);
    onChange?.(null);
    inputRef.current?.focus();
  };

  const handleAdditiveOptionSelect = (e: SyntheticEvent) => {
    if (!additiveOption) {
      return;
    }

    e.stopPropagation();
    e.preventDefault();
    if (mouseEvent === "onMouseDown" && e.type === "mousedown") {
      suppressNextMouseUpAndClick();
    }

    handleSelect({ ...additiveOption, value: !!inputText?.trim() ? inputText : additiveOption.value });

    // Slight delay to allow the option to be added & list to be expanded before scrolling
    setTimeout(() => {
      const addedOption = optionsListRef.current?.lastElementChild;
      scrollToReveal(addedOption as HTMLElement);
    }, 50);

    return;
  };

  function AdditiveOption() {
    if (!additiveOption) {
      return null;
    }

    return (
      <div
        title={additiveOption.title || additiveOption.label}
        className={cx(styles.option, "option", styles.isAdditive)}
        {...{
          [mouseEvent]: handleAdditiveOptionSelect,
        }}
      >
        {additiveOption.icon ?? null}
        <label>{additiveOption.label}</label>
        {!!inputText?.trim() && (
          <span className={styles.isAdditiveLabel}>{truncateStringWithEllipses(inputText, 16)}</span>
        )}
      </div>
    );
  }

  function SearchResultsPlaceholder() {
    if ((additiveOption && !filteredOptions.length) || filteredOptions.length) {
      return null;
    }

    return <div className={cx(styles.option, styles.noResults)}>No results</div>;
  }

  return (
    <div
      className={cx(
        "select",
        styles.pseudoWrapper,
        {
          styles,
          disabled: lockIcon || disabled,
          open: openMenu,
          hasThumbnail,
          up: up && !!openMenu,
          optionsGap: showOptionsGap && !!openMenu,
        },
        styles[selectSize],
        inputState === "info" && styles.info,
        className,
      )}
    >
      <Tooltip.Root delayDuration={tooltipDelay}>
        <Tooltip.Trigger>
          <div
            className={cx(
              styles.button,
              !!activeOption?.icon &&
                !activeOption?.displayBehavior &&
                !activeOption?.specialBehavior &&
                styles.hasActiveOptionIcon,
              showClearControl && styles.hasClearControl,
              "button",
              hideInput && styles.hideInput,
            )}
            aria-label={ariaLabel}
          >
            <Input
              id={id}
              ref={inputRef}
              value={inputText ?? ""}
              autoComplete={autoComplete}
              placeholder={placeholder}
              disabled={lockIcon || disabled}
              noDisabledCursor={lockIcon}
              title={lockIcon || disabled ? title : undefined}
              transparent={transparent}
              data-testid={dataTestId}
              onFocus={(e) => {
                isInputFocusedRef.current = true;
                e.target.select();
                setOpenMenu(true);
              }}
              onBlur={() => {
                isInputFocusedRef.current = false;
                setInputTextFromValue();
                closeMenu();
              }}
              onChange={(e) => {
                const formattedValue = inputFormatter?.(e.target.value) ?? e.target.value;
                setInputText(formattedValue);
                inputOnChange?.(formattedValue);
              }}
              inputSize={selectSize === "small" ? "medium" : selectSize}
              onKeyDown={(e) => {
                switch (e.code) {
                  case CODE_ESCAPE: {
                    if (openMenu) {
                      e.preventDefault();
                      e.stopPropagation();
                      setInputTextFromValue();
                      setOpenMenu(false);
                    }
                    return;
                  }
                  case CODE_TAB:
                  case CODE_ENTER: {
                    if (e.code === CODE_ENTER) {
                      e.preventDefault();
                    }
                    if (!openMenu) {
                      return;
                    }
                    if (e.shiftKey && e.code === CODE_TAB) {
                      return;
                    }

                    let chosenOption: OptionProps | undefined;

                    if (!!inputText?.trim() && !filteredOptions.length && additiveOption) {
                      return handleAdditiveOptionSelect(e as any);
                    } else {
                      chosenOption = filteredOptions[optionIndex];
                    }

                    if (chosenOption === undefined || !chosenOption.intermediate) {
                      inputRef.current?.blur();
                    }
                    handleSelect(chosenOption);

                    return;
                  }
                  case CODE_ARROWUP: {
                    // prevent default to keep cursor position for the input
                    e.preventDefault();
                    if (!openMenu) {
                      setOpenMenu(true);
                    } else {
                      const newIndex = (optionIndex > 0 ? optionIndex - 1 : 0) || 0;
                      updateActive(newIndex);
                    }
                    return;
                  }
                  case CODE_ARROWDOWN: {
                    e.preventDefault();
                    if (!openMenu) {
                      setOpenMenu(true);
                    } else {
                      const newIndex = (optionIndex < filteredOptions.length - 1 ? optionIndex + 1 : optionIndex) ?? 0;
                      updateActive(newIndex);
                    }
                    return;
                  }
                  case CODE_SHIFTLEFT:
                  case CODE_SHIFTRIGHT:
                  case CODE_CTRLLEFT:
                  case CODE_CTRLRIGHT:
                  case CODE_ALTLEFT:
                  case CODE_ALTRIGHT:
                  case CODE_METALEFT:
                  case CODE_METARIGHT:
                  case CODE_ARROWLEFT:
                  case CODE_ARROWRIGHT:
                  case CODE_HOME:
                  case CODE_END:
                  case CODE_PRINTSCREEN:
                  case CODE_CAPSLOCK:
                    return;
                  default: {
                    if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "a" || e.key.toLowerCase() === "c")) {
                      // User can select the text with Ctrl+A, or copy it with Ctrl+C
                      return;
                    }
                    if (
                      (e.code === CODE_BACKSPACE || e.code === CODE_DELETE) &&
                      window.getSelection()?.type === "Range"
                    ) {
                      // Allow user to select part of the text and then delete just that part.
                      return;
                    }
                  }
                }
              }}
              onKeyUp={(e) => {
                switch (e.code) {
                  case CODE_ESCAPE:
                  case CODE_ARROWUP:
                  case CODE_ARROWDOWN:
                  case CODE_ENTER:
                  case CODE_TAB:

                  case CODE_SHIFTLEFT:
                  case CODE_SHIFTRIGHT:
                  case CODE_CTRLLEFT:
                  case CODE_CTRLRIGHT:
                  case CODE_ALTLEFT:
                  case CODE_ALTRIGHT:
                  case CODE_METALEFT:
                  case CODE_METARIGHT:
                  case CODE_HOME:
                  case CODE_END:
                  case CODE_PRINTSCREEN:
                  case CODE_CAPSLOCK:
                    return;
                  default: {
                    const userInput = inputRef.current?.value;

                    if (!userInput) {
                      setFilteredOptions(addNewOptionsOnly.length > 0 ? defaultFilteredOptions : defaultOptions);
                      return;
                    }

                    let newOptions: OptionProps[];

                    if (typeof options === "function") {
                      newOptions = options(userInput);
                    } else {
                      newOptions = [];
                      const userInputLower = userInput.toLowerCase();
                      for (const option of optionsExcludingAddNew) {
                        if (
                          option.label.toLowerCase().includes(userInputLower) ||
                          option.title?.toLowerCase().includes(userInputLower)
                        ) {
                          newOptions.push(option);
                        }
                      }
                      if (newOptions.length === 0 && addNewOptionsOnly.length > 0) {
                        newOptions = addNewOptionsOnly;
                      }
                    }

                    setFilteredOptions(newOptions);
                    setOptionIndex(0);
                    setOpenMenu(true);
                  }
                }
              }}
            />
            {activeOption?.icon && !activeOption.displayBehavior && !activeOption.specialBehavior && (
              <div className={styles.optionIcon}>{activeOption.icon}</div>
            )}
            <div className={styles.iconWrapper} onMouseDown={(e) => e.preventDefault()}>
              {canClear && (
                <button
                  type="button"
                  className={styles.clearButton}
                  aria-label="Clear selection"
                  data-testid="select-clear"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleClear}
                >
                  <IconClose size={10} color="stone" />
                </button>
              )}
              {lockIcon ? (
                <IconLocked variant="portal" style={{ marginBottom: "1px" }} color="stone" size={10} />
              ) : (
                !hideInput &&
                !disabled && (
                  <button
                    type="button"
                    className={styles.toggleButton}
                    aria-label={openMenu ? "Close options" : "Open options"}
                    onClick={() => {
                      if (!openMenu) {
                        inputRef.current?.focus();
                        setOpenMenu(true);
                      } else {
                        closeMenu();
                      }
                    }}
                  >
                    <IconMiniChevronDown rotate={openMenu ? 180 : 0} color="stone" />
                  </button>
                )
              )}
            </div>

            {hasThumbnail && (
              <div className={styles.thumbnailWrapper}>
                <Thumbnail {...activeOption?.thumbnail} size="tiny" />
              </div>
            )}
          </div>
        </Tooltip.Trigger>
        {tooltipEnabled && (
          <Tooltip.Content>
            <div className={styles.tooltipContent}>{tooltipText ?? inputText}</div>
          </Tooltip.Content>
        )}
      </Tooltip.Root>

      <div
        className={cx(styles.options, "options", openMenu && styles.open)}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        ref={optionsRef}
        style={{ maxHeight: optionsMaxHeight }}
      >
        <SearchResultsPlaceholder />
        {openMenu && additiveOption ? <AdditiveOption /> : null}
        {openMenu ? (
          <div
            className={cx(styles.optionsList, !showScrollbar && styles.hideScrollbar, optionsClassName)}
            ref={optionsListRef}
            style={{ maxWidth: optionsMaxWidth, maxHeight: optionsMaxHeight }}
            data-testid="select-options-list"
          >
            {filteredOptions.map((o, i) => {
              const isSelectedOption = !!o.label && value === o.value;
              const thumbnail = o.thumbnail && (
                <Thumbnail label={o.label} {...o.thumbnail} className={styles.thumbnail} />
              );
              const indentStyle = o.indent
                ? { paddingLeft: OPTION_PADDING_LEFT + o.indent * OPTION_INDENT_STEP }
                : undefined;

              if (!o.displayBehavior) {
                const hasSubmenu = o.subOptions !== undefined && o.subOptions.length > 0;
                const optionContent = (
                  <>
                    {hasSubmenu && (
                      <IconChevron className={cx(styles.submenuChevron, styles.submenuChevronLeft)} size={9} />
                    )}
                    {thumbnail}
                    {o.color && <IconCircle size={10} fill={o.color} />}
                    {o.icon}
                    <label>
                      {!o.placeLabelIconRight && o.labelIcon}
                      {o.label}
                      {o.intermediate ? <div className={styles.intermediateArrow}>&gt;</div> : ""}
                      {o.subLabel && <div className={styles.subLabel}>{o.subLabel}</div>}
                    </label>
                    {o.placeLabelIconRight && o.labelIcon && <span className={styles.labelRight}>{o.labelIcon}</span>}
                    {showSelectedOptionCheck && !hasSubmenu && isSelectedOption && (
                      <IconCheck className={styles.checkedOption} />
                    )}
                  </>
                );
                const isSubmenuOption = hasSubmenu && onSubmenuOpen !== undefined;
                return (
                  <div
                    key={`${o.value}-${i}`}
                    title={isSubmenuOption ? undefined : o.title || o.label}
                    style={indentStyle}
                    className={cx(
                      styles.option,
                      "option",
                      !isSubmenuOption && isSelectedOption && styles.selected,
                      optionIndex === i && styles.active,
                      thumbnail && styles.optionThumbnail,
                    )}
                    {...(isSubmenuOption
                      ? {
                          onMouseEnter: (e) => {
                            onSubmenuOpen({
                              anchorRect: (e.currentTarget as HTMLElement).getBoundingClientRect(),
                              subOptions: o.subOptions ?? [],
                              onSelect: (value) => {
                                onChange?.(value);
                                closeMenu();
                              },
                            });
                          },
                          onMouseLeave: () => onSubmenuMouseLeaveRow?.(),
                          onMouseDown: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          },
                        }
                      : {
                          [mouseEvent]: (e: MouseEvent<HTMLDivElement>) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (mouseEvent === "onMouseDown") {
                              suppressNextMouseUpAndClick();
                            }
                            if (inputRef.current && !o.intermediate) {
                              inputRef.current?.blur();
                            }
                            handleSelect(o, i);
                          },
                        })}
                  >
                    {optionContent}
                  </div>
                );
              } else if (
                o.displayBehavior === "header" ||
                o.displayBehavior === "disabled" ||
                o.displayBehavior === "locked"
              ) {
                return (
                  <div
                    key={`${o.value}-${i}`}
                    title={o.title || o.label}
                    id={`select-header-${o.label}`}
                    style={indentStyle}
                    className={cx(
                      styles.option,
                      "option",
                      styles[o.displayBehavior],
                      thumbnail && styles.optionThumbnail,
                    )}
                  >
                    {thumbnail}
                    {o.icon}
                    <label>{o.label}</label>
                    {o.displayBehavior === "locked" && <IconLocked variant="portal" color="stone" size={10} />}
                  </div>
                );
              } else if (o.displayBehavior === "ruler") {
                return <HorizontalRule key={`${o.value}-${i}`} />;
              }

              return null;
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function Select(props: SelectProps) {
  return <SelectComponent {...props} />;
}
